import type { RsiMomentumRules } from "@/types";

export type RulesForm = Required<Omit<RsiMomentumRules, "notes">>;
export type NumKey = { [K in keyof RulesForm]: RulesForm[K] extends number ? K : never }[keyof RulesForm];

/** The rule set proposed in the strategy review (wider ATR stop, 1R partial, 2R final, trail). */
export const PROPOSED_RULES: RulesForm = {
  stop_mode: "ATR",
  atr_stop_multiplier: 1.0,
  reward_risk_ratio: 2.0,
  opposite_signal_exit: true,
  reverse_on_opposite_signal: true,
  candle_quality_enabled: true,
  close_position_pct: 30,
  first_target_enabled: true,
  first_target_r: 1.0,
  book_fraction: 0.5,
  move_stop_to_breakeven: true,
  trailing_enabled: true,
  trailing_atr_multiple: 1.0,
  max_trades_per_day: 2,
};

export function readRules(effective: Record<string, unknown> | null): RulesForm {
  const sec = (k: string) => ((effective?.[k] ?? {}) as Record<string, unknown>);
  const sub = (o: Record<string, unknown>, k: string) => ((o[k] ?? {}) as Record<string, unknown>);
  const entry = sec("entry"), exit = sec("exit"), risk = sec("risk");
  const cq = sub(entry, "candle_quality"), ft = sub(exit, "first_target"), tr = sub(exit, "trailing_stop");
  const num = (v: unknown, d: number) => (typeof v === "number" ? v : d);
  const bool = (v: unknown, d: boolean) => (typeof v === "boolean" ? v : d);
  return {
    stop_mode: exit.stop_mode === "ATR" ? "ATR" : "SIGNAL_BAR_OR_ATR",
    atr_stop_multiplier: num(exit.atr_stop_multiplier, 0.35),
    reward_risk_ratio: num(exit.reward_risk_ratio, 1.5),
    opposite_signal_exit: bool(exit.opposite_signal_exit, true),
    reverse_on_opposite_signal: bool(exit.reverse_on_opposite_signal, true),
    candle_quality_enabled: bool(cq.enabled, false),
    close_position_pct: num(cq.close_position_pct, 30),
    first_target_enabled: bool(ft.enabled, false),
    first_target_r: num(ft.r_multiple, 1.0),
    book_fraction: num(ft.book_fraction, 0.5),
    move_stop_to_breakeven: bool(ft.move_stop_to_breakeven, true),
    trailing_enabled: bool(tr.enabled, false),
    trailing_atr_multiple: num(tr.atr_multiple, 1.0),
    max_trades_per_day: num(risk.max_trades_per_day, 3),
  };
}

/** Client-side checks mirroring the server's validation (the server still decides). */
export function rulesErrors(f: RulesForm): string[] {
  const out: string[] = [];
  const pos = (v: number, label: string, max: number) => {
    if (!Number.isFinite(v) || v <= 0 || v > max) out.push(`${label} must be greater than 0 and at most ${max}`);
  };
  pos(f.atr_stop_multiplier, "ATR stop multiple", 10);
  pos(f.reward_risk_ratio, "Final target (R)", 20);
  if (f.candle_quality_enabled) pos(f.close_position_pct, "Close position %", 50);
  if (f.first_target_enabled) {
    pos(f.first_target_r, "First target (R)", 20);
    if (f.first_target_r >= f.reward_risk_ratio) out.push("First target must be below the final target");
    if (!(f.book_fraction >= 0 && f.book_fraction <= 1)) out.push("Book % must be 0–100");
  }
  if (f.trailing_enabled) {
    if (!f.first_target_enabled) out.push("Trailing stop needs the first target (it starts after it)");
    pos(f.trailing_atr_multiple, "Trail ATR multiple", 10);
  }
  if (!Number.isInteger(f.max_trades_per_day) || f.max_trades_per_day < 1 || f.max_trades_per_day > 20) out.push("Max trades per day must be 1–20");
  return out;
}


/** One-line description of the ACTIVE rules for the page header (never hard-coded). */
export function rulesSummary(effective: Record<string, unknown> | null): string {
  const f = readRules(effective);
  const risk = ((effective?.risk ?? {}) as Record<string, unknown>);
  const eod = typeof risk.eod_square_off_time === "string" ? risk.eod_square_off_time : "15:15";
  const parts = [
    "NIFTY intraday · 5-minute bars · EMA(3)/WMA(21) of RSI(9) · VWAP + EMA(20) filters",
    f.candle_quality_enabled ? `candle filter ${f.close_position_pct}%` : null,
    f.stop_mode === "ATR" ? `${f.atr_stop_multiplier}× ATR stop` : `signal-bar/${f.atr_stop_multiplier}× ATR stop`,
    f.first_target_enabled
      ? `${f.first_target_r}R first target (book ${Math.round(f.book_fraction * 100)}%${f.move_stop_to_breakeven ? ", stop to breakeven" : ""})`
      : null,
    f.trailing_enabled ? `${f.trailing_atr_multiple}× ATR trail` : null,
    `${f.reward_risk_ratio}R target`,
    `max ${f.max_trades_per_day} trades/day`,
    `${eod} IST square-off`,
  ];
  return parts.filter(Boolean).join(" · ");
}
